import { useEffect, useRef, useState } from 'react';
import LinearProgress from '@mui/material/LinearProgress';
import Modal from './Modal';

/**
 * @typedef Props
 * @prop {string} title
 * @prop {string} description
 * @prop {string} itemNamePlural
 * @prop {number} itemCount
 * @prop {(progress: number) => void} onCancel
 * @prop {() => void} onFinish
 * @prop {(index: number) => Promise<void>} onProcessIndex
 * @prop {boolean} open
 */

/**
 * @param {Props} props
 */
export default function BulkActionModal({
  title,
  description,
  itemNamePlural,
  itemCount,
  onCancel,
  onFinish,
  onProcessIndex,
  open,
}) {
  const [busy, setBusy] = useState(open);
  const [progress, setProgress] = useState(0);

  // Refs to store stable references to callback functions
  const onCancelRef = useRef(onCancel);
  const onFinishRef = useRef(onFinish);
  const onProcessIndexRef = useRef(onProcessIndex);

  // Update refs when props change
  useEffect(() => {
    onCancelRef.current = onCancel;
    onFinishRef.current = onFinish;
    onProcessIndexRef.current = onProcessIndex;
  }, [onCancel, onFinish, onProcessIndex]);

  const reset = () => {
    setProgress(0);
    setBusy(false);
  };

  useEffect(() => {
    const finishedProcessing = progress === itemCount;

    if (!busy) {
      if (progress !== 0 && !finishedProcessing) {
        onCancelRef.current(progress);
        reset();
      }
    } else if (finishedProcessing) {
      onFinishRef.current();
      reset();
    } else {
      onProcessIndexRef
        .current(progress)
        .then(() => {
          setProgress(i => i + 1);
        })
        .catch(error => {
          console.error(error);
          onCancelRef.current(progress);
          reset();
        });
    }
  }, [itemCount, busy, progress]);

  useEffect(() => {
    setBusy(open);
  }, [open]);

  return (
    <Modal
      onClose={() => {
        setBusy(false);
      }}
      open={busy}>
      <h2
        className="tw-text-2xl tw-font-bold tw-text-gray-700 tw-mb-2"
        id="modal-title">
        {title}
      </h2>
      <p id="modal-description">{description}</p>
      <div className="tw-my-6 tw-border tw-border-gray-400 tw-rounded tw-overflow-hidden">
        <div className="tw-flex tw-items-center tw-p-4">
          <div className="tw-flex-auto">
            Processing:
            <span className="tw-ml-2 tw-text-gray-700 tw-font-medium">
              {progress} of {itemCount} {itemNamePlural}
            </span>
          </div>
          <div className="gb-loader gb-loader--secondary" />
        </div>
        <LinearProgress
          color="secondary"
          value={Math.floor((progress / itemCount) * 100)}
          variant="determinate"
        />
      </div>
      <div className="tw-flex tw-justify-end tw-mt-4">
        <button
          className="gb-btn gb-btn--lg"
          onClick={() => {
            setBusy(false);
          }}>
          Cancel
        </button>
      </div>
    </Modal>
  );
}
